import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import Button from '@components/smartAdminUI/Button';
import useInterval from '@hooks/useInterval';

import Alert from 'react-bootstrap/Alert';

const RowLockMessage = ({
  rowLockEntity,
  message,
  lockedBy,
  lockedByMe,
  isAdmin,
  checkStatus = () => null,
  onTakeover = () => null,
  onDiscard = () => null,
  displayActions = true,
  lastUpdatedOn = null,
  lastCheckedOn = null,
  isAutoSave = false,
}) => {
  const [minutesElapsed, setMinutesElapsed] = useState(null);
  const canTakeAction = useMemo(() => isAutoSave || minutesElapsed >= 5, [minutesElapsed, isAutoSave, lockedByMe]);
  const timeElapsedMessage = useMemo(() => {
    if (minutesElapsed == null) return null;
    if (minutesElapsed < 1) return 'moments ago';
    if (minutesElapsed >= 60) {
      const hours = Math.floor(minutesElapsed / 60);
      const minutes = minutesElapsed % 60;
      return minutes > 0
        ? `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes > 1 ? 's' : ''} ago`
        : `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    return `${minutesElapsed} minute${minutesElapsed > 1 ? 's' : ''} ago`;
  }, [minutesElapsed]);

  const lastUpdate = useMemo(() => lastUpdatedOn && new Date(lastUpdatedOn), [lastUpdatedOn]);

  const updateElapsedMinutes = useCallback(() => {
    if (!lastUpdate) return;
    const minutes = Math.floor((Math.abs(Date.now() - lastUpdate) / 1000) / 60);
    setMinutesElapsed(minutes);
  }, [lastUpdate]);

  useEffect(updateElapsedMinutes, [lastUpdate]);
  useInterval(updateElapsedMinutes, lastUpdate ? (1000 * 30) : null);

  const [callback, setCallback] = useState(null);
  const [holdCallbackFiring, setHoldCallbackFiring] = useState(false);

  const handleTakeover = () => {
    checkStatus();
    setCallback(() => onTakeover);
    setHoldCallbackFiring(true); // put the callback firing on hold until the status check is complete
  };

  const handleDiscard = () => {
    checkStatus();
    setCallback(() => onDiscard);
    setHoldCallbackFiring(true); // put the callback firing on hold until the status check is complete
  };

  useEffect(() => {
    if (callback && holdCallbackFiring) setHoldCallbackFiring(false);
  }, [lastCheckedOn]);

  useEffect(() => {
    if (callback && !holdCallbackFiring && (isAdmin || canTakeAction)) {
      callback();
      setCallback(null);
    }
  }, [holdCallbackFiring]);

  return (
    <div className="row">
      <div className="col">
        <Alert variant={`${isAutoSave ? 'success' : 'danger'} fade show`}>
          {rowLockEntity && <h1>{`${rowLockEntity.type.charAt(0).toUpperCase() + rowLockEntity.type.slice(1)} [${rowLockEntity.id}]`}</h1>}
          <h3>
            <i className="far fa-user-lock mr-2" />
            {message || (isAutoSave
              ? 'There is auto-save data available.'
              : `This entity is locked by ${lockedBy}.`)}
          </h3>
          {!isAutoSave && lastUpdate && timeElapsedMessage && <p>{`The lock was last updated on ${lastUpdate.toLocaleString()} (${timeElapsedMessage}).`}</p>}
          {(isAdmin || (displayActions && canTakeAction)) && (
            <>
              {isAutoSave
                ? <p>Do you wish to use the available auto-save data?</p>
                : <p>Do you wish to take over and create a new lock, or remove the existing lock?</p>}
              <Button name="btnRowLockTakeOver" variant="primary" onClick={handleTakeover}>
                <i className="fas fa-lock mr-2" aria-hidden="true" />
                Take Over
              </Button>
              <Button name="btnRemoveLock" variant="primary" onClick={handleDiscard}>
                <i className="fas fa-lock-open mr-2" aria-hidden="true" />
                Remove Lock
              </Button>
            </>
          )}
        </Alert>
      </div>
    </div>
  );
};

RowLockMessage.propTypes = {
  rowLockEntity: PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.number,
  }),
  message: PropTypes.string,
  lockedBy: PropTypes.string,
  lockedByMe: PropTypes.bool,
  isAdmin: PropTypes.bool,
  checkStatus: PropTypes.func,
  onTakeover: PropTypes.func,
  onDiscard: PropTypes.func,
  displayActions: PropTypes.bool,
  lastUpdatedOn: PropTypes.string,
  lastCheckedOn: PropTypes.number,
  isAutoSave: PropTypes.bool,
};

export default RowLockMessage;
